import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import '../styles/auth.css';
import logo from '../assets/logo.png';

const ParentQuickSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!firstName.trim() || !lastName.trim() || !username.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (!termsAgreed || !privacyAgreed) {
      setError('Please agree to Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      // Get birth_date from auth metadata
      const meta = user?.user_metadata || {};
      const birthDate = meta.birth_date || null;

      // Upsert parent profile
      const { data: parentData, error: upsertError } = await supabase
        .from('users')
        .upsert({
          auth_id: user.id,
          email: user.email,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          username: username.trim(),
          birth_date: birthDate,
          is_under_18: false,
          is_parent: true,
          parent_will_trade: false,
          signup_type: 'parent',
          intake_completed: true,
          contract_agreed: true,
          contract_agreed_at: new Date().toISOString(),
        }, { onConflict: 'auth_id' })
        .select('id')
        .single();

      if (upsertError) throw upsertError;

      const parentId = parentData.id;

      // Read kids from auth metadata and create child rows + family_links
      const kids = meta.kids || [];
      for (const kid of kids) {
        if (!kid.name || !kid.name.trim()) continue;

        // Create child user row
        const { data: childData, error: childError } = await supabase
          .from('users')
          .insert({
            first_name: kid.name.trim(),
            is_under_18: true,
            has_own_auth: false,
            intake_completed: true,
          })
          .select('id')
          .single();

        if (childError) {
          console.error('Error creating child:', childError);
          continue;
        }

        // Create family link
        await supabase.from('family_links').insert({
          parent_id: parentId,
          child_id: childData.id,
          can_trade: true,
        });
      }

      navigate('/profile-picker');
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openLegal = (type) => {
    const url = type === 'terms' ? '/terms' : '/privacy';
    window.open(url, '_blank');
  };

  return (
    <div className="quick-setup-container">
      <div className="quick-setup-content">
        <div className="quick-setup-header">
          <img
            src={logo}
            alt="CardChase"
            style={{
              width: 80,
              height: 80,
              borderRadius: 16,
              marginBottom: 12,
              objectFit: 'cover'
            }}
          />
          <h1 className="quick-setup-title">Quick Setup</h1>
          <p className="quick-setup-subtitle">
            Set up your parent account to manage your kids' trading profiles
          </p>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form className="quick-setup-form" onSubmit={handleSubmit}>
          <div className="input-group">
            <label className="input-label">First Name</label>
            <input
              type="text"
              className="auth-input"
              placeholder="Your first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="input-group">
            <label className="input-label">Last Name</label>
            <input
              type="text"
              className="auth-input"
              placeholder="Your last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="input-group">
            <label className="input-label">Username</label>
            <input
              type="text"
              className="auth-input"
              placeholder="Pick a username"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
              disabled={loading}
            />
          </div>

          <div className="quick-setup-legal">
            <input
              type="checkbox"
              className="quick-setup-checkbox"
              checked={termsAgreed}
              onChange={(e) => setTermsAgreed(e.target.checked)}
              disabled={loading}
            />
            <span className="quick-setup-legal-text">
              I agree to the{' '}
              <span className="quick-setup-legal-link" onClick={() => openLegal('terms')}>
                Terms of Service
              </span>
            </span>
          </div>

          <div className="quick-setup-legal">
            <input
              type="checkbox"
              className="quick-setup-checkbox"
              checked={privacyAgreed}
              onChange={(e) => setPrivacyAgreed(e.target.checked)}
              disabled={loading}
            />
            <span className="quick-setup-legal-text">
              I agree to the{' '}
              <span className="quick-setup-legal-link" onClick={() => openLegal('privacy')}>
                Privacy Policy
              </span>
            </span>
          </div>

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? <div className="spinner" /> : 'Get Started'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ParentQuickSetup;
